import { createResource } from './resource'
import type {
  Business,
  Grievance,
  Meeting,
  Opportunity,
  Organization,
  Project,
  Stakeholder,
  DocRecord,
  User,
  Activity,
  Intake,
} from '@/types'
import { getCollection, setCollection, withLatency, nextId } from '@/services/mock/db'
import {
  businessFromApi,
  businessToApi,
  documentFromApi,
  documentToApi,
  grievanceFromApi,
  grievanceToApi,
  intakeFromApi,
  intakeToApi,
  meetingFromApi,
  meetingToApi,
  opportunityFromApi,
  opportunityToApi,
  organizationFromApi,
  organizationToApi,
  projectFromApi,
  projectToApi,
  stakeholderFromApi,
  stakeholderToApi,
  userFromApi,
  userToApi,
} from '@/services/http/mappers'
import { httpClient } from '@/services/http/client'
import { USING_API } from '@/services/config'

/**
 * Central API surface. Each resource mirrors a REST endpoint
 * (`GET /stakeholders`, `POST /stakeholders`, …). Replace the
 * `createResource` mock with axios calls to go live.
 */

export const stakeholdersApi = createResource<Stakeholder>({
  collection: 'stakeholders',
  idPrefix: 'stk',
  searchable: ['firstName', 'lastName', 'phone', 'email', 'occupation', 'constituency', 'address.district', 'address.village'],
  // Backend-ready: the mock 'stakeholders' collection is served by API '/contacts'.
  endpoint: 'contacts',
  backend: true,
  fromApi: stakeholderFromApi,
  toApi: stakeholderToApi as (p: unknown) => unknown,
})

export const businessesApi = createResource<Business>({
  collection: 'businesses',
  idPrefix: 'biz',
  searchable: ['name', 'industry', 'sector', 'address.district'],
  endpoint: 'businesses',
  backend: true,
  fromApi: businessFromApi,
  toApi: businessToApi as (p: unknown) => unknown,
})

export const organizationsApi = createResource<Organization>({
  collection: 'organizations',
  idPrefix: 'org',
  searchable: ['name', 'type', 'address.district'],
  endpoint: 'organizations',
  backend: true,
  fromApi: organizationFromApi,
  toApi: organizationToApi as (p: unknown) => unknown,
})

export const opportunitiesApi = createResource<Opportunity>({
  collection: 'opportunities',
  idPrefix: 'opp',
  searchable: ['title', 'sector', 'location.district'],
  endpoint: 'opportunities',
  backend: true,
  fromApi: opportunityFromApi,
  toApi: opportunityToApi as (p: unknown) => unknown,
})

export const projectsApi = createResource<Project>({
  collection: 'projects',
  idPrefix: 'prj',
  searchable: ['name', 'department', 'location.district'],
  endpoint: 'projects',
  backend: true,
  fromApi: projectFromApi,
  toApi: projectToApi as (p: unknown) => unknown,
})

export const grievancesApi = createResource<Grievance>({
  collection: 'grievances',
  idPrefix: 'grv',
  searchable: ['reference', 'title', 'category', 'citizenName', 'department', 'location.district'],
  endpoint: 'grievances',
  backend: true,
  fromApi: grievanceFromApi,
  toApi: grievanceToApi as (p: unknown) => unknown,
})

export const meetingsApi = createResource<Meeting>({
  collection: 'meetings',
  idPrefix: 'mtg',
  searchable: ['title', 'type', 'location'],
  endpoint: 'meetings',
  backend: true,
  fromApi: meetingFromApi,
  toApi: meetingToApi as (p: unknown) => unknown,
})

// Documents need real file upload/download, which the generic resource can't
// express. So we keep the resource for list/all/get/update/remove, but override
// `create` (multipart, no file) and add `upload` (multipart, with file) and
// `download`. In mock mode everything falls back to the in-browser resource.
const documentsResource = createResource<DocRecord>({
  collection: 'documents',
  idPrefix: 'doc',
  searchable: ['name', 'category', 'ocrText'],
  endpoint: 'documents',
  backend: true,
  fromApi: documentFromApi,
  toApi: documentToApi as (p: unknown) => unknown,
})

/** POST a document as multipart (meta JSON + optional file) and map the result. */
async function uploadDocumentHttp(meta: Partial<DocRecord>, file: File | null): Promise<DocRecord> {
  const form = new FormData()
  form.append('meta', JSON.stringify(documentToApi(meta)))
  if (file) form.append('file', file)
  const res = await httpClient.post('/documents', form, { headers: { 'Content-Type': 'multipart/form-data' } })
  return documentFromApi(res.data)
}

/** Trigger a browser download of a document's real file (API mode). */
async function downloadDocumentHttp(doc: DocRecord): Promise<void> {
  const res = await httpClient.get(`/documents/${doc.id}/download`, { responseType: 'blob' })
  const url = URL.createObjectURL(res.data as Blob)
  const a = document.createElement('a')
  a.href = url
  a.download = doc.name
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

/** Mock download — emits a small text stub (no real bytes are stored). */
function downloadDocumentMock(doc: DocRecord): void {
  const content = `MCCMS — Document export\nName: ${doc.name}\nType: ${doc.type}\nCategory: ${doc.category}`
  const url = URL.createObjectURL(new Blob([content], { type: 'text/plain;charset=utf-8' }))
  const a = document.createElement('a')
  a.href = url
  a.download = doc.name
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

export const documentsApi = {
  list: documentsResource.list,
  all: documentsResource.all,
  get: documentsResource.get,
  update: documentsResource.update,
  remove: documentsResource.remove,
  /** Create a document. With a file it's a real upload; without, metadata-only. */
  create: (payload: Partial<DocRecord>): Promise<DocRecord> =>
    USING_API ? uploadDocumentHttp(payload, null) : documentsResource.create(payload),
  upload: (file: File, meta: Partial<DocRecord>): Promise<DocRecord> =>
    USING_API ? uploadDocumentHttp(meta, file) : documentsResource.create({ ...meta, size: file.size || meta.size }),
  /** Trigger a browser download of the document's file. */
  download: (doc: DocRecord): Promise<void> =>
    USING_API ? downloadDocumentHttp(doc) : Promise.resolve(downloadDocumentMock(doc)),
}

/**
 * Voice recording for the Front Desk form. Speech-to-text happens in the
 * browser and the recording is attached via the Documents API — there's no
 * dedicated backend call. `available` gates the mic button to API mode (the
 * recording needs the Documents backend).
 */
export const voiceApi = {
  available: USING_API,
}

export const intakesApi = createResource<Intake>({
  collection: 'intakes',
  idPrefix: 'itk',
  searchable: ['reference', 'subject', 'fromName', 'fromPhone', 'note'],
  endpoint: 'intakes',
  backend: true,
  fromApi: intakeFromApi,
  toApi: intakeToApi as (p: unknown) => unknown,
})

const usersResource = createResource<User>({
  collection: 'users',
  idPrefix: 'usr',
  searchable: ['name', 'email', 'designation'],
  endpoint: 'users',
  backend: true,
  fromApi: userFromApi,
  toApi: userToApi as (p: unknown) => unknown,
})

export const usersApi = {
  ...usersResource,
  /** Admin action: set a user's password directly (no current password needed). */
  resetPassword: async (id: string, newPassword: string): Promise<void> => {
    if (USING_API) {
      await httpClient.post(`/users/${id}/reset-password`, { newPassword })
    }
  },
  // Override remove: surface the backend's message (e.g. "Administrator
  // accounts can't be deleted…") instead of axios's generic error text.
  remove: async (id: string): Promise<{ id: string }> => {
    try {
      return await usersResource.remove(id)
    } catch (e: any) {
      throw new Error(e?.response?.data?.detail ?? 'Could not delete user')
    }
  },
}

/** Surface shared by the mock and http activity feeds. */
interface ActivitiesApi {
  forEntity(entityId: string, entityType?: string): Promise<Activity[]>
  recent(limit?: number): Promise<Activity[]>
  add(input: Omit<Activity, 'id'>): Promise<Activity>
}

// --- In-browser mock (VITE_DATA_SOURCE=mock) --------------------------------
const mockActivitiesApi: ActivitiesApi = {
  forEntity: (entityId, entityType) =>
    withLatency(() =>
      (getCollection('activities') as Activity[])
        // Match on both type and id: entity ids are only unique *within* a type
        // (a contact, an org and a grievance can all share id "2"), so filtering
        // by id alone would leak one entity's activities onto another's timeline.
        .filter((a) => a.entity.id === entityId && (!entityType || a.entity.type === entityType))
        .sort((a, b) => (a.at < b.at ? 1 : -1)),
    ),
  recent: (limit = 20) => withLatency(() => (getCollection('activities') as Activity[]).slice(0, limit)),
  add: (input) =>
    withLatency(() => {
      const list = getCollection('activities') as Activity[]
      const created: Activity = { ...input, id: nextId('act', list) }
      setCollection('activities', [created, ...list])
      return created
    }),
}

// --- Real backend (VITE_DATA_SOURCE=api) ------------------------------------
// The backend uses numeric ids; keep them as strings on the frontend.
const activityFromApi = (raw: any): Activity => ({
  id: String(raw.id),
  entity: { type: raw.entity.type, id: String(raw.entity.id), label: raw.entity.label ?? '' },
  kind: raw.kind,
  title: raw.title,
  body: raw.body ?? undefined,
  at: raw.at,
  actorId: raw.actorId ?? '',
  actorName: raw.actorName ?? '',
  meta: raw.meta ?? undefined,
})

const httpActivitiesApi: ActivitiesApi = {
  forEntity: async (entityId, entityType) =>
    (await httpClient.get<any[]>('/activities', { params: { entityId, entityType } })).data.map(activityFromApi),
  recent: async (limit = 20) =>
    (await httpClient.get<any[]>('/activities', { params: { limit } })).data.map(activityFromApi),
  add: async (input) =>
    activityFromApi(
      (
        await httpClient.post('/activities', {
          entity: { type: input.entity.type, id: String(input.entity.id), label: input.entity.label },
          kind: input.kind,
          title: input.title,
          body: input.body ?? null,
          at: input.at,
          actorId: input.actorId,
          actorName: input.actorName,
          meta: input.meta ?? null,
        })
      ).data,
    ),
}

/** Read the polymorphic activity feed, optionally scoped to an entity. */
export const activitiesApi: ActivitiesApi = USING_API ? httpActivitiesApi : mockActivitiesApi
